import { useCallback } from "react";
import { container } from "tsyringe";
import { DocumentArrowUpIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/20/solid";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import Modal from "components/elements/Modal";
import Typography from "components/elements/Typography";
import Form, { useForm } from "components/materials/Form";
import DragAndDropFile from "components/materials/Form/DragAndDropFileElement";
import Input from "components/materials/Form/InputElement";
import I18n from "components/materials/I18n";
import { EForumPostCategory } from "common/enums/Project/Config/Forum/EForumPostCategory";
import ForumPostRequestResource from "common/resources/Project/Config/Forum/ForumPostRequestResource";
import ForumPostResponseResource from "common/resources/Project/Config/Forum/ForumPostResponseResource";
import { useProjectId } from "hooks/useProjectId";
import ForumPostService from "services/projects/ForumPostService";
import classes from "./classes.module.scss";
import { TextArea } from "components/materials/Form/TextAreaElement";

type IProps = {
	category: EForumPostCategory;
	isOpen: boolean;
	onClose: () => void;
	onCreated: (post: ForumPostResponseResource) => void;
};

const forumPostService = container.resolve(ForumPostService);

export default function CreatePostModal(props: IProps) {
	const { formRef, errors, handleError, clearErrors } = useForm();
	const { projectId } = useProjectId();

	const onCreatePost = useCallback(
		(formData: { [key: string]: unknown }) => {
			clearErrors();

			const forumPostRequestResource = ForumPostRequestResource.hydrate<ForumPostRequestResource>({
				title: [{ lang: I18n.getLang(), text: formData["title"] as string }],
				description: [{ lang: I18n.getLang(), text: formData["description"] as string }],
				category: props.category,
			});

			const image = (formData["image"] as FileList)?.[0];

			forumPostRequestResource
				.validateOrReject()
				.then(() => {
					forumPostService.create(projectId, forumPostRequestResource, image).then(props.onCreated).catch(handleError);
				})
				.catch(handleError);
		},
		[clearErrors, handleError, projectId, props],
	);

	return (
		<Modal isOpen={props.isOpen} onClose={props.onClose} className={classes["root"]}>
			<div className={classes["header"]}>
				<Typography type="p" size="large" weight="bold">
					{I18n.asset.pages.professional_space.tabs.forum.create_post_modal.title}
				</Typography>
				<XMarkIcon className={classes["icon"]} onClick={props.onClose} />
			</div>

			<Form onSubmit={onCreatePost} errors={errors} formRef={formRef} className={classes["form-container"]}>
				<div className={classes["inputs-container"]}>
					<Input name="title" required label={I18n.asset.pages.professional_space.tabs.forum.create_post_modal.post_title} className={classes["input"]} />
					<TextArea name="description" required label={I18n.asset.pages.professional_space.tabs.forum.create_post_modal.post_description} className={classes["input"]} />
					<DragAndDropFile
						name="image"
						icon={<DocumentArrowUpIcon className={classes["drag-and-drop-icon"]} />}
						accept={["image/png", "image/jpeg", "image/webp"]}
						sub_description={`(max. 5${I18n.asset.unit.megabyte})`}
						maxMbSize={5}
						label={I18n.asset.pages.professional_space.tabs.forum.create_post_modal.post_image}
						description={I18n.asset.pages.professional_space.tabs.forum.create_post_modal.post_image_description}
						formRef={formRef}
						className={classes["drag-and-drop"]}
					/>
				</div>

				<div className={classes["buttons-container"]}>
					<Button variant={EButtonVariant.TEXT} color={EButtonColor.PRIMARY} onClick={props.onClose}>
						{I18n.asset.pages.professional_space.tabs.forum.create_post_modal.cancel}
					</Button>
					<Button type="submit" variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY}>
						{I18n.asset.pages.professional_space.tabs.forum.create_post_modal.create_post}
					</Button>
				</div>
			</Form>
		</Modal>
	);
}
